import React, { useEffect, useState, FC } from "react";
import { useDispatch, useSelector } from "react-redux";
import * as url from "../../../../api/url";
import APIutil from "../../../../api/APIutils";
import { getAllResources } from "../../../../action/cultureAction";
import {
  Box,
  FormControl,
  Grid,
  Link,
  MenuItem,
  Paper,
  Select,
  SelectChangeEvent,
  TextField,
  Typography,
  Button
} from "@mui/material";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogTitle from "@mui/material/DialogTitle";
import "Assets/Reports/EventMarkers.css";
import EnsembleColorBlocks from "../../../../components/EnsembleComponents/Common/EnsembleColorBlocks";
import { EventMarkerItem, EventMarkerTypeDefinitions } from "./EventMarker.d";
import CancelIcon from "@mui/icons-material/Cancel";
import { useDebounce } from "use-debounce";
import { setAmplitudeEvent } from "../../../../amplitude/amplitude";

export interface EventMarkerTypeProps {
  typeId: number;
  typeName: string;
  typeLabel: string;
  typeColor: string;
  setTypeIdValue: Function;
  setTypeNameValue: Function;
  setTypeLabelValue: Function;
  setTypeColorValue: Function;
  setShowEdit: Function;
  isEdit: boolean;
  instancesOfType: number;
  setErrors: Array<boolean>;
  duplicateValue: Function;
  eventMarkerData: Array<EventMarkerItem>;
}

export const EventMarkerType: FC<EventMarkerTypeProps> = (props) => {
  const {
    typeId,
    typeName,
    typeLabel,
    typeColor,
    setTypeIdValue,
    setTypeNameValue,
    setTypeLabelValue,
    setTypeColorValue,
    isEdit,
    setShowEdit,
    instancesOfType,
    setErrors,
    duplicateValue,eventMarkerData
  } = props;

  const dispatch = useDispatch();

  const labels = useSelector(
    (state: any) =>
      state.CulturalLabelsRootReducer.ReportMgmt_LabelsBasedOnPreference
  );

  const addNew = labels !== null ? `+ ${labels["AddNew.Text"]}` : "+ Add New";
  const selectOne = labels !== null ? "- " + labels["SelectOne.Text"] + " -" : "- Select One -";
  const [eventMarkerTypeList, setEventMarkerTypeList] = useState<
    Array<EventMarkerTypeDefinitions>
  >([]);
  const [showFields, setShowFields] = useState<boolean>(false);
  const [showLabels, setShowLabels] = useState<boolean>(false);
  const [localTypeName, setLocalTypeName] = useState(typeName);
  const [localTypeLabel, setLocalTypeLabel] = useState(typeLabel);
  const [localTypeColor, setLocalTypeColor] = useState(typeColor);
  const [debouncedEventName] = useDebounce(localTypeName, 600);

  // these are used so that add New fields are not checked immediately but existing types are
  const [displayTextErrors, setDisplayTextErrors] = useState(isEdit);
  const [displayLabelErrors, setDisplayLabelErrors] = useState(isEdit);
  const [showAsError, setShowAsError] = useState<boolean>(false);
  const [duplicate, setDuplicate] = useState(false as Boolean);
  const [labelText, setLabelText] = useState("" as string);
  const [acronymTextValue, setAcronymTextValue] = useState("" as string);
  const [labelTextStyle, setLabelTextStyle] = useState("" as string);
  const [acronymTextStyle, setAcronymTextStyle] = useState("" as string);
  const [confirmationText, setConfirmationText] = useState("" as string);
  const previousLabel=typeLabel;
  const previousName=typeName;
  
  const returnToCreateScreen = (show): void => {
    setShowEdit(show);
  };

  useEffect(() => {
    if (debouncedEventName !== "") {
      const duplicate = Boolean(
        eventMarkerTypeList.find(
          ({ eventMarkerTypeTitle }) =>
            eventMarkerTypeTitle === debouncedEventName
        )
      );
      setDuplicate(duplicate && !isEdit);
    }
  }, [debouncedEventName]);

  useEffect(() => {
    setUAText("typeText");
    setEventHelperTextClass("typeText");
    setDisplayTextErrors(setErrors[1]);
    duplicateValue(duplicate);
  }, [duplicate]);

  useEffect(() => {
    let confirmation: string =
      instancesOfType > 1
        ? labels["EventMarker.Save.Confirmation.Text"]
        : labels["EventMarker.Save.ConfirmationSingle.Text"];
    confirmation = confirmation.replace("{0}", instancesOfType.toString());
    setConfirmationText(confirmation);
  }, []);


  
  // dialog strings amd functions
  const [open, setOpen] = React.useState(false);
  const yesText = labels["ReportManagement.Yes.Text"];
  const noText = labels["ReportManagement.No.Text"];
  let isRequiredText: string = labels["IsRequired.Text"]
    ? labels["IsRequired.Text"]
    : " is required.";
  const confirmationTitle = labels["AreYouSure.Text"];
  const UA25Char = labels !== null ? labels["Limit25Characters.Text"] : "Event Name";
  const UARequired =  labels !== null ? labels["Required.Text"] :"Required";
  const UA3Char = labels !== null ? labels["Limit3Characters.Text"] : "Marker Label";

  const handleClickOpen = () => {
    if (validInputs()) {
      const labelCount = eventMarkerData.filter(
        ({ eventMarkerTypeAcronym, eventMarkerTypeTitle }) =>
        eventMarkerTypeAcronym.trim() === previousLabel.trim() && eventMarkerTypeTitle.trim() === previousName.trim()
      ).length

      let confirmation: string =
      labelCount > 1
        ? labels["EventMarker.Save.Confirmation.Text"]
        : labels["EventMarker.Save.ConfirmationSingle.Text"];
    confirmation = confirmation.replace("{0}", labelCount.toString());
    setConfirmationText(confirmation);

      setOpen(true);
    }
  };

  const handleClose = () => {
    setOpen(false);
  };

  const handleSave = () => {
    setOpen(false);
    saveMarkerType();
  };

  async function getEventMarkerItem() {
    await APIutil.getDataAPI(url.get_EventMarkerType_List)
      .then((res) => {
        let eMarkerTypes: Array<EventMarkerTypeDefinitions> = [];

        res.forEach((r) => {
          let setAsSelected =
            r.eventMarkerTypeId !== ""
              ? parseInt(r.eventMarkerTypeId) === typeId
              : false;
          eMarkerTypes.push({
            eventMarkerTypeId: r.eventMarkerTypeId,
            eventMarkerTypeTitle: r.eventMarkerTypeTitle,
            eventMarkerTypeAcronym: r.eventMarkerTypeAcronym,
            eventMarkerTypeColor: r.eventMarkerTypeColor,
            canBeDeleted: r.canBeDeleted,
            isSelected: setAsSelected,
          });
        });

        let addNewItem = {
          eventMarkerTypeId: 0,
          eventMarkerTypeTitle: addNew,
          eventMarkerTypeAcronym: "",
          eventMarkerTypeColor: "C31200",
          canBeDeleted: false,
          isSelected: false,
        };

        eMarkerTypes.splice(0, 0, addNewItem);

        setEventMarkerTypeList(eMarkerTypes);
      })
      .catch((error) => {
        throw error;
      });
  }

  const populateForm = (e: SelectChangeEvent) => {
    if (e.target.value !== "") {
      let tId: number = parseInt(e.target.value);
      setShowLabels(tId > 0 && !isEdit);
      populateFormForEventMarker(tId);
    }
  };

  const populateFormForEventMarker = (eventMarkerId: number) => {
    if (eventMarkerId >= 0) {
      setShowAsError(false);
      let fdn = eventMarkerTypeList.find(
        (evt) => evt.eventMarkerTypeId === eventMarkerId
      );
      if (fdn && fdn !== null) {
        if (fdn.eventMarkerTypeTitle === addNew) {
          let defaultColor: string = "FF0000";
          setLocalTypeName("");
          setLocalTypeLabel("");
          setLocalTypeColor(defaultColor);
          setTypeIdValue(0);
          setDisplayTextErrors(false);
          setDisplayLabelErrors(false);
          setTypeNameValue("");
          setTypeLabelValue("");
          setTypeColorValue(defaultColor);
        } else {
          setTypeNameValue(fdn.eventMarkerTypeTitle);
          setTypeLabelValue(fdn.eventMarkerTypeAcronym);
          setTypeColorValue(fdn.eventMarkerTypeColor);
          setTypeIdValue(fdn.eventMarkerTypeId);
          setDisplayTextErrors(true);
          setDisplayLabelErrors(true);
        }
      }
    } else {
      setLocalTypeName("");
      setLocalTypeLabel("");
      setLocalTypeColor("");
      setTypeIdValue(-1);
      setDisplayTextErrors(false);
      setDisplayLabelErrors(false);
    }
  };

  useEffect(() => {
    dispatch(getAllResources());
    getEventMarkerItem();
    setUAText("typeText");
    setUAText("acronymText");
    setEventHelperTextClass("typeText");
    setEventHelperTextClass("acronymText");
  }, []);

  useEffect(() => {
    setShowFields(typeId === 0 || isEdit);
    setShowLabels(typeId > 0 && !isEdit);
  }, [typeId, isEdit]);

  useEffect(() => {
    setShowAsError(setErrors[0]);
    setDisplayTextErrors(setErrors[1]);
    setDisplayLabelErrors(setErrors[2]);
  }, [setErrors]);

  const selectThisColor = (hexColor: string) => {
    if (hexColor !== "" && hexColor !== localTypeColor) {
      setLocalTypeColor(hexColor);

      if (!isEdit && typeId === 0)
        // push change to parent
        setTypeColorValue(hexColor);
    }
  };

  // instead of creating individual state values we can create an object and set the values within the object making sure to map the previous state within the setState
  /**  setEventMarkerType(
    (prev) =>(
      {...prev,
      localTypeName: targetValue}
    )
  )*/
  const changeInputValue = (targetId: string, targetValue: string) => {
    switch (targetId) {
      case "textFieldEventMarkerName":
        if (targetValue !== localTypeName) {
          setLocalTypeName(targetValue);
          if (!isEdit && typeId === 0) {
            setDisplayTextErrors(true);
            setTypeNameValue(targetValue); // push change to parent
          }
        }
        break;
      case "textFieldEventMArkerAcronymText":
        if (targetValue !== localTypeLabel) {
          setLocalTypeLabel(targetValue);
          if (!isEdit && typeId === 0) {
            setDisplayLabelErrors(true);
            setTypeLabelValue(targetValue); // push change to parent
          }
        }
       break;
      default:
        break;
    }
  };

  function displayOptions() {
    return eventMarkerTypeList.map((td, i) => {
      return (
        <MenuItem
          value={td.eventMarkerTypeId}
          key={td.eventMarkerTypeId}
          selected
        >
          {td.eventMarkerTypeTitle}
        </MenuItem>
      );
    });
  }

  function setFieldDisable() {
    return typeId < 0;
  }

  function cancelChanges() {
    returnToCreateScreen(false);
  }

  const validInputs = () => {
    return okEventName && okEventLabel;
  };

  const setUAText = (targetId: string) => {
    let uaText = "";
    switch (targetId) {
      case "typeText":
        if (okEventName || localTypeName.length > 25) {
          setLabelText(labels["Limit25Characters.Text"]);
        } else {
          setLabelText(isRequiredText.replace("{0}", labels["EventName.Text"]));
        }

        if (duplicate) {
          setLabelText(labels["DuplicateValue.Text"]);
        }
        break;
      case "acronymText":
        uaText =
          okEventLabel || localTypeLabel.length > 0
            ? labels["Limit3Characters.Text"]
            : isRequiredText.replace("{0}", labels["MarkerLabel.Text"]);
        setAcronymTextValue(uaText);
        break;
      default:
        break;
    }
  };

  let okEventName: boolean = !displayTextErrors
    ? true
    : localTypeName !== "" &&
      localTypeName !== null &&
      localTypeName.length <= 25;

  let okEventLabel: boolean = !displayLabelErrors
    ? true
    : localTypeLabel !== "" &&
      localTypeLabel !== null &&
      localTypeLabel.length <= 3;

  const setEventHelperTextClass = (targetId: string) => {
    switch (targetId) {
      case "typeText":
        setLabelTextStyle(
          okEventName && !duplicate ? "labelUAItalic" : "labelUAError"
        );
        break;
      case "acronymText":
        setAcronymTextStyle(okEventLabel ? "labelUAItalic" : "labelErrorRight");
        break;
      default:
        setAcronymTextStyle("");
        break;
    }
  };

  const setEventLabelClass = (targetId: string) => {
    let classname = "";
    switch (targetId) {
      case "typeText":
        classname = okEventName ? "labelStandard" : "labelError";
        break;
      case "acronymText":
        classname = okEventLabel ? "labelStandard" : "labelError";
        break;
      case "markerType":
        classname = showAsError ? "labelError" : "labelStandard";
        break;
      default:
        break;
    }
    return classname;
  };

  async function saveMarkerType(): Promise<void> {
    const markerTypeDetails = {
      TypeId: typeId,
      TypeName: localTypeName,
      TypeLabel: localTypeLabel,
      TypeColor: localTypeColor,
    };

    APIutil.postData(url.post_EventMarkerType, markerTypeDetails)
      .then(() => {
        setAmplitudeEvent("Event Marker Type Edit");
        setTypeNameValue(localTypeName);
        setTypeLabelValue(localTypeLabel);
        setTypeColorValue(localTypeColor);

        returnToCreateScreen(false);
      })
      .catch((error) => {
        throw error;
      });
  }

  return (
    <div style={{ width: "460px" }}>
      <Paper
        variant="outlined"
        sx={{
          width: "100%",
          borderRadius: isEdit ? "none" : "8px 8px 8px 8px",
          backgroundColor: isEdit ? "#f5f6fa" : "white",
          border: isEdit ? "none" : "1px",
        }}
      >
        <Grid
          item
          xs={12}
          pl={1}
          pt={2}
          style={{ marginLeft: "10px", paddingTop: "5px" }}
        >
          {isEdit ? (
            <div>
              <Typography variant="h5">
                {labels["EditEventType.Text"]}
              </Typography>
              <div>
                <Link
                  id="returnToCreateScreen"
                  onClick={(e) => {
                    returnToCreateScreen(false);
                    e.preventDefault();
                  }}
                  style={{ cursor: "pointer" }}
                >
                  {labels["Buttons.Back.Text"]}
                </Link>
                <div style={{ paddingTop: "14px", fontSize: "14px" }}>
                  {labels["EditEventType.UA.Text"]}
                </div>
              </div>
            </div>
          ) : (
            <div>
              <div style={{ display: "flex", width: "100%" }}>
                <div id="markerLabel" className="evSubHeader">
                  {labels["Marker.Text"]}
                </div>
                <div
                  style={{
                    display: isEdit || typeId <= 0 ? "none" : "block",
                    width: "48%",
                    textAlign: "right",
                  }}
                >
                  <div style={{ textAlign: "right" }}>
                    <Link
                      id="linkEventMarkerEdit"
                      component="button"
                      sx={{ fontSize: "14px" }}
                      underline="none"
                      onClick={(e) => {
                        returnToCreateScreen(true);
                        e.preventDefault();
                      }}
                    >
                      {labels["Edit.Text"]}
                    </Link>
                  </div>
                </div>
              </div>
              <div>
                <div className={setEventLabelClass("markerType")}>
                  {labels["MarkerType.Text"]}
                </div>
                <Grid item xs={7}>
                  <FormControl
                    id="eventMarkerTypeFormControl"
                    fullWidth={true}
                    error={showAsError}
                    required={true}
                  >
                    <Select
                      id="selectEventMarkerEventType"
                      value={typeId.toString()}
                      displayEmpty={true}
                      labelId="markerLabelId"
                      onChange={populateForm}
                      sx={{
                        width: "95%",
                        height: "36px",
                        backgroundColor: "white",
                        "& legend": { display: "none" },
                        "& fieldset": { top: 0 },
                      }}
                      renderValue={(value) => {
                        const item = eventMarkerTypeList.find(
                          ({ eventMarkerTypeId }) =>
                            eventMarkerTypeId.toString() === value.toString()
                        );
                        return item
                          ? item.eventMarkerTypeTitle
                          : value.toString() === "0"
                          ? addNew
                          : selectOne;
                      }}
                    >
                      {displayOptions()}
                    </Select>
                  </FormControl>
                  <div
                    style={{ display: showAsError ? "inline-block" : "none" }}
                  >
                    <CancelIcon className="errorIcon" color="error" />
                    <span className="labelError">
                      {isRequiredText.replace("{0}", labels["MarkerType.Text"])}
                    </span>
                  </div>
                </Grid>
              </div>
            </div>
          )}

          {showFields ? (
            <Grid
              container
              item
              spacing={2}
              style={{ paddingTop: "30px", paddingRight: "20px" }}
            >
              <Grid item xs={9}> 
                <div className="labelStandard" style={{marginBottom: '0px'}}>
                  {labels["EventName.Text"]}
                </div>
              <TextField
                    id="textFieldEventMarkerName"
                    className="inputFull"
                    size="small"
                    sx={{
                      "& legend": { display: "none" },
                      "& fieldset": { top: 0 },
                    }}
                    variant="outlined"
                    onChange={(event) => {
                      changeInputValue(event.target.id, event.target.value);
                    }}
                    fullWidth={true}
                    defaultValue={localTypeName}
                    required={true}
                    error={!okEventName}
                    inputProps={{ maxLength: 25, height: '36px', border: "none"}}
                  
                  />
                  {okEventName ? (
                    <div className="labelUAItalic">{UA25Char}</div>
                    ) : (
                    <div className="labelUAItalic" style={{color: 'red'}}>{UARequired}</div>
                  )}
              </Grid>
              <Grid item xs={3}>
                <div className="labelStandard" style={{marginBottom: '0px'}}>
                  {labels["MarkerLabel.Text"]}
                </div>
              <TextField
                    id="textFieldEventMArkerAcronymText"
                    className="inputShort"
                    defaultValue={localTypeLabel}
                    size="small"
                    onChange={(event) => {
                      changeInputValue(event.target.id, event.target.value);
                    }}
                    sx={{
                      "& legend": { display: "none" },
                      "& fieldset": { top: 0 },
                    }}
                    error={!okEventLabel}
                    inputProps={{ maxLength: 3, height: '36px', border: "none"}}
                  />
                   {okEventLabel ? (
                    <div className="labelUAItalic">{UA3Char}</div>
                    ) : (
                    <div className="labelUAItalic" style={{color: 'red'}}>{UARequired}</div>
                  )}
              </Grid>

              <Grid item xs={12} style={{ paddingTop: "30px" }} pl={1}>
                <div className="labelStandard">
                  {labels["DisplayColor.Text"]}
                </div>
                <EnsembleColorBlocks
                  setColor={selectThisColor}
                  currentSelection={localTypeColor}
                  disableValue={setFieldDisable()}
                />
              </Grid>

              <div
                style={{ display: isEdit ? "block" : "none", width: "100%", marginBottom: isEdit ? "200px" : "0px" }}
              >
                <Grid item xs={12}>
                  <Box display={"flex"} justifyContent="right">
                    <Box pr={2}>
                      <Button
                        id="buttonEventMarkerEditCancel"
                        className="eventMarkerButton eventMarkerButtonLg eventMarkerCancelButton"
                        variant="outlined"
                        onClick={(e) => {
                          cancelChanges();
                          e.preventDefault();
                        }}
                      >
                        {labels["Buttons.Cancel.Text"]}
                      </Button>
                    </Box>
                    <Box>
                      <Button
                        id="buttonEventMarkerEditSave"
                        className="eventMarkerButton eventMarkerButtonLg"
                        variant="contained"
                        onClick={(e) => {
                          handleClickOpen();
                          e.preventDefault();
                        }}
                      >
                        {labels["Buttons.Save.Text"]}
                      </Button>
                    </Box>
                  </Box>
                </Grid>
              </div>
            </Grid>
          ) : (
            <Grid style={{ paddingTop: "32px", paddingRight: "20px" }}>
              {showLabels ? (
                <div>
                  <div style={{ display: "flex", width: "100%" }}>
                    <div style={{ width: "75%" }}>
                      <div className="labelStandard">
                        {labels["EventName.Text"]}
                      </div>
                      <div style={{ fontWeight: "bold", fontSize: "16px" }}>
                        {typeName}
                      </div>
                    </div>

                    <div style={{ width: "25%" }}>
                      <div className="labelStandard">
                        {labels["MarkerLabel.Text"]}
                      </div>
                      <div style={{ fontWeight: "bold", fontSize: "16px" }}>
                        {typeLabel}
                      </div>
                    </div>
                  </div>

                  <div style={{ paddingTop: "28px", width: "100%" }}>
                    <div className="labelStandard">
                      {labels["DisplayColor.Text"]}
                    </div>
                    <ul className="colorBlockHolder">
                      <li key={1} className="colorBlockListItem">
                        <div
                          style={{ backgroundColor: "#" + typeColor }}
                          className="colorBlocks"
                        >
                          <span className="colorBlockCheckMarkHidden"></span>
                        </div>
                      </li>
                    </ul>
                  </div>
                </div>
              ) : (
                <div></div>
              )}
            </Grid>
          )}

          <Grid id="saveDialog" item xs={12}>
            <Dialog
              open={open}
              onClose={handleClose}
              aria-labelledby="alert-dialog-title"
              aria-describedby="alert-dialog-description"
              fullWidth={true}
              maxWidth="xs"
            >
              <DialogTitle id="alert-dialog-title">
                <Typography variant="h6">{confirmationTitle}</Typography>
              </DialogTitle>
              <DialogContent>
                <DialogContentText id="alert-dialog-description">
                  <span style={{ paddingTop: "14px", fontSize: "14px" }}>
                    {confirmationText}
                  </span>
                </DialogContentText>
              </DialogContent>
              <DialogActions sx={{ margin: "14px", marginTop: "24px" }}>
              <Button
                  className="eventMarkerButton eventMarkerCancelButton"
                  id="diag_cancel_button"
                  variant="outlined"
                  onClick={handleClose}
                >
                  {noText}
                </Button>
                <Button
                  className="eventMarkerButton"
                  id="diag_save_button"
                  variant="contained"
                  onClick={handleSave}
                  autoFocus
                >
                  {yesText}
                </Button>
              </DialogActions>
            </Dialog>
          </Grid>
        </Grid>
      </Paper>
    </div>
  );
};

export default EventMarkerType;
